 import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dropdown } from 'primereact/dropdown';
import { useFormik } from 'formik';
import UserAddressService from '../../services/useraddress/useraddress.service';
import { StateDropdownDTO } from '../../models/cust-address/cust-address-model';
import { useTranslation } from 'react-i18next';

interface AddDistrictProps {
    showDistrictDialog: boolean;
    hideDistrictDialog: () => void;
    handleAddSuccess: () => void;
}

const AddDistrict: React.FC<AddDistrictProps> = ({
    showDistrictDialog,
    hideDistrictDialog,
    handleAddSuccess
}) => {
    const { t } = useTranslation();
    const [states, setStates] = useState<StateDropdownDTO[]>([]);
    const [loading, setLoading] = useState(false);
    const [filterValue, setFilterValue] = useState('');
    const toast = React.useRef<Toast>(null);
   
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchStates(filterValue);
        }, 300);

        return () => clearTimeout(timer);
    }, [filterValue]);

    const fetchStates = async (keyword: string = '') => {
        try {
            setLoading(true);
            const response = await UserAddressService.searchStates({ keyword });
            setStates(response);
        } catch (error) {
            showMessage('error', t('error'), t('failedToLoadStates'));
            setStates([]);
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (
        severity: 'success' | 'error',
        summary: string,
        detail: string
    ) => {
        toast.current?.show({
            severity,
            summary,
            detail
        });
    };

    const formik = useFormik({
        initialValues: {  
            stateId: "",
            districtName: "",
        },
        validate: (data) => {
            const errors: Record<string, string> = {};
            
            if (!data.stateId) {
                errors.stateId = 'State Required';
            }
            if (!data.districtName) {
                errors.districtName = 'District Name Required';
            }
            
            return errors;
        },
        onSubmit: async (data, { resetForm }) => {
            const districtData = {
                districtName: data.districtName,
                stateId: data.stateId
               
            };
            
            try {
                const response = await UserAddressService.addNewDistrict(districtData);
                if (response?.id) {
                    showMessage('success', t('Success'), t('District Added Successfully'));
                    setTimeout(() => {
                        handleAddSuccess();
                        hideDistrictDialog();
                        resetForm();
                      }, 1000);  
                } else {
                    showMessage('error', t('error'), t('District Already Exists'));
                }
            } catch (error) {
                showMessage('error', t('error'), t('Error Creating District'));
            }
        },
    });

    const handleCloseModal = () => {
        hideDistrictDialog();
        formik.resetForm();
        setFilterValue("");
    };

    const dialogHeader = (
        <Button
            className="p-button-text p-button-rounded p-button-plain"
            onClick={handleCloseModal}
            aria-label="Close"
        />
    );
    
    return (
        <Dialog
            visible={showDistrictDialog}
            className="quickview w-50"
            breakpoints={{ '960px': '75vw', '641px': '90vw' }}
            modal
            onHide={handleCloseModal}
            closable={true}
            header={dialogHeader}
        >
                  <h5 className="border-round order-top-color p-2">Add New District</h5>
                <br/>

            <div className="flex flex-wrap">
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="stateId" className="label-semi-bold">
                            State<span className="required">*</span>
                        </label>
                        <br/>
                        <Dropdown
                            id="stateId"
                            name="stateId"
                            value={formik.values.stateId}
                            onChange={(e) => {
                                if (e.value !== null) {
                                    formik.setFieldValue("stateId", e.value);
                                }
                            }}
                            options={states.length > 0 ? states : [{ display: `No options available `, stateId: null }]}
                            optionLabel="display"
                            optionValue="stateId"
                            placeholder='Select State'
                            className={formik.errors.stateId ? 'p-invalid' : ''}
                            style={{ width: '100%' }}
                            filter
                            filterBy="display"
                            showClear
                            onFilter={(e) => setFilterValue(e.filter)}
                            filterPlaceholder="Search states..."
                            emptyFilterMessage={''} 
                        />
                        <br/>
                         {formik.errors.stateId && (
                            <small className="p-error">{formik.errors.stateId}</small>
                        )}  

                    </div> 
                </div>
                <div className="w-full mb-3 px-3">
                    <div className="field">
                        <label htmlFor="districtName" className="label-semi-bold">
                            District Name<span className="required">*</span>
                        </label>
                        <br/>
                        <InputText
                           // keyfilter="alphanum"
                            id="districtName"
                            name="districtName"
                            value={formik.values.districtName}
                            onChange={formik.handleChange}
                            onKeyDown={(e) => {
                                if (!/^[a-zA-Z ]$/.test(e.key) && e.key !== 'Backspace') {
                                    e.preventDefault();
                                }
                            }}
                            placeholder='District Name'
                            className={formik.errors.districtName ? 'p-invalid' : ''}
                            style={{ width: '100%' }}
                        />
                        <br/>
                        {formik.errors.districtName && (
                            <small className="p-error">{formik.errors.districtName}</small>
                        )}
                    </div>
                </div>
            </div>

            <div className="text-right mt-4">
                <Button
                    label={t('Save')}
                    onClick={() => formik.handleSubmit()}
                    className="bg-green-700 text-white"
                    disabled={loading}
                />
            </div>
            <Toast ref={toast}/>
        </Dialog>
    );
};

export default AddDistrict;

